<script setup lang="ts">
    import { languages, fileTypes } from '~/labels';
    import type { Product } from '~/types';

    const { isMobile } = useViewport();

    const preferredLanguage = usePreferredLanguage();
    const {
        globalAssets,
        addAssets,
        removeAssets,
        assetsPreviewPath,
        assetsDownloadPath,
    } = useGlobalAssets();

    const { product, isOnListPage = false } = defineProps<{
        product: Product;
        isOnListPage?: boolean;
    }>();

    const { allDocs, filteredDocs, filteredGroupedByType } = useExtractAssets(product);

    function downloadAll() {
        if (filteredDocs.value.length > 1) {
            const check = confirm(
                `Are you sure you want to download all the ${filteredDocs.value.length} assets at once?`
            );

            if (!check) {
                return;
            }
        }

        filteredDocs.value.forEach(asset => {
            const link = document.createElement('a');
            link.target = '_blank';
            link.href = assetsDownloadPath + asset.id;
            link.download = asset.name;
            link.click();
        });
    }

    function addToCart() {
        addAssets(filteredDocs?.value);
    }

    function removeAllFromCart() {
        removeAssets(filteredDocs?.value);
    }

    function openCard() {
        if (isOnListPage) {
            navigateTo(product.product_key);
        }
    }

    function getProductName() {
        return product?._highlightResult?.name?.value ?? product.name;
    }

    function getProductItems() {
        return (
            product?._highlightResult?.items.map(i => i.value).join(', ') ??
            product.items.join(', ')
        );
    }
</script>

<template>
    <article
        class="flex flex-col items-center rounded-lg border-2 bg-[#F3F4F6] p-6 sm:flex-row sm:items-start sm:justify-between"
    >
        <!-- Left container -->
        <div class="mb-4 sm:w-[70%]">
            <header>
                <NuxtLink
                    v-if="isOnListPage"
                    :to="product.product_key"
                >
                    <h2
                        class="mb-2 select-all text-2xl font-bold"
                        v-html="getProductName()"
                        :class="{
                            'cursor-pointer': isOnListPage,
                        }"
                    ></h2>
                </NuxtLink>

                <div v-else>
                    <h2
                        class="mb-2 select-all text-2xl font-bold"
                        v-html="getProductName()"
                        :class="{
                            'cursor-pointer': isOnListPage,
                        }"
                    ></h2>
                </div>

                <div class="py-4 sm:pb-4 sm:pt-0">
                    Item numbers:
                    <span
                        class="pb-4"
                        v-html="getProductItems()"
                    ></span>
                </div>
            </header>

            <div
                v-if="product.image_id && isMobile"
                class="class- order-1 my-12 w-full min-w-min max-w-sm sm:order-2 sm:w-1/3"
                @click="openCard"
                :class="{
                    'cursor-pointer': isOnListPage,
                }"
            >
                <img
                    :src="assetsPreviewPath + product.image_id"
                    :alt="product.name"
                    class="h-full w-full rounded-lg dark:opacity-75 md:object-cover"
                    loading="lazy"
                />
            </div>

            <section>
                <div class="flex flex-col items-center gap-4 md:flex-row">
                    <!-- Product description -->
                    <div class="order-2 flex-1 gap-4 sm:order-1">
                        <!-- This causes hydration mismatch so we run a replacer -->
                        <p class="">
                            {{ product.description?.replace(/\s+/g, ' ') }}
                        </p>

                        <!-- Labels -->
                        <ul
                            v-if="product.labels"
                            class="mt-4 flex flex-wrap items-center gap-8"
                        >
                            <LabelImage
                                v-for="label in product.labels"
                                :label="label"
                            >
                                <span class="text-base-content hover:underline">
                                    {{ label }}
                                </span>
                            </LabelImage>
                        </ul>
                    </div>
                </div>
            </section>

            <section
                v-if="allDocs.length"
                class="mt-4"
            >
                <!-- Columns by file type and then by file by language -->
                <div class="grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-4">
                    <div
                        v-for="(docs, type) in filteredGroupedByType"
                        class="mt-4 flex flex-col gap-2"
                    >
                        <h3 class="font-semibold text-base-content">
                            {{ fileTypes[type] }}
                        </h3>
                        <div
                            v-for="file in docs"
                            :title="file.name"
                            class="form-control tooltip"
                            :data-tip="file.name"
                        >
                            <label class="label cursor-pointer justify-start gap-2">
                                <input
                                    type="checkbox"
                                    v-model="globalAssets"
                                    :value="file"
                                    class="checkbox rounded-lg border-black"
                                />
                                <span class="label-text truncate">
                                    {{ languages[file.language] }}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- If no documents -->
                <div
                    v-if="filteredDocs.length === 0 && preferredLanguage"
                    class="alert alert-error"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6 shrink-0 stroke-current"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span>
                        There are no documents for
                        {{ preferredLanguage.map(l => languages[l]).join(', ') }}.
                        Consider changing the language in the top right dropdown.
                    </span>
                    <button
                        class="btn btn-sm"
                        @click="preferredLanguage = []"
                    >
                        Reset to default
                    </button>
                </div>

                <div
                    v-if="filteredDocs.length"
                    class="mt-4"
                >
                    <button
                        @click="downloadAll"
                        class="btn btn-outline join-item btn-sm"
                    >
                        Download all
                    </button>
                </div>
            </section>
        </div>

        <!-- Right container -->
        <div
            v-if="product.image_id && !isMobile"
            class="class- order-1 w-full min-w-min max-w-sm sm:order-2 sm:w-1/3"
            @click="openCard"
            :class="{
                'cursor-pointer': isOnListPage,
            }"
        >
            <img
                :src="assetsPreviewPath + product.image_id"
                :alt="product.name"
                class="h-full w-full rounded-lg dark:opacity-75 md:object-cover"
                loading="lazy"
            />
        </div>
    </article>
</template>

<style scoped>
::v-deep(mark) {
    font-weight: bold;
    background-color: transparent;
    text-decoration: underline;
    text-decoration-color: #1C33BC;
    text-decoration-thickness: 4px;
}
</style>
